import React, { useState } from "react"

interface TabsContextValue {
  activeTab: string
  setActiveTab: (tab: string) => void
}

const TabsContext = React.createContext<TabsContextValue | null>(null)

interface TabsProps {
  defaultValue: string
  children: React.ReactNode
}

export function Tabs({ defaultValue, children }: TabsProps) {
  const [activeTab, setActiveTab] = useState(defaultValue)

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div>{children}</div>
    </TabsContext.Provider>
  )
}

interface TabsListProps {
  children: React.ReactNode
  className?: string
}

export function TabsList({ children, className }: TabsListProps) {
  return (
    <div className={`flex w-full space-x-1 ${className || ''}`}>
      {children}
    </div>
  )
}

interface TabsTriggerProps {
  value: string
  children: React.ReactNode
  className?: string
}

export function TabsTrigger({ value, children, className }: TabsTriggerProps) {
  const context = React.useContext(TabsContext)
  if (!context) {
    throw new Error('TabsTrigger must be used within Tabs')
  }

  const { activeTab, setActiveTab } = context
  const isActive = activeTab === value

  const activeClasses = 'bg-purple-700 text-white dark:bg-purple-600'
  const inactiveClasses = 'text-gray-600 dark:text-gray-400 hover:bg-purple-200 dark:hover:bg-purple-900/50'
  const baseClasses = 'px-4 py-2.5 rounded-md cursor-pointer transition-colors duration-150 text-sm font-medium flex-1 text-center'

  return (
    <button
      onClick={() => setActiveTab(value)}
      className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses} ${className || ''}`}
    >
      {children}
    </button>
  )
}

interface TabsContentProps {
  value: string
  children: React.ReactNode
  className?: string
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const context = React.useContext(TabsContext)
  if (!context) {
    throw new Error('TabsContent must be used within Tabs')
  }

  const { activeTab } = context
  
  if (activeTab !== value) {
    return null
  }

  return <div className={className}>{children}</div>
}